import { TextField, TableRow, Button, ListItem, ListItemText, TableCell, Collapse, Tooltip, ListItemAvatar, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import BlockIcon from '@mui/icons-material/Block';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import './RequestActivityRow.css';
import { Box } from '@mui/system';
import { ArrowForwardIosSharp, Info } from '@mui/icons-material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import EquivalencyState from '../../EquivalencyState';
import InfoDialog from '../../../../components/InfoDialog/InfoDialog';
import RequestFileRow from '../RequestFilesContainer/RequestFileRow/RequestFileRow';
import FileReaderProvider, { FileReaderButton } from '../FileReader/FileReader';

const getClassNameByState = (state) => {
  const className = 'EQXXI-RequestActivityRow-ActivityState-Tooltip ';
  switch (state) {
    case EquivalencyState.rejected:
      return className + 'State-Rejected';
    case EquivalencyState.aproved:
      return className + 'State-Approved';
    default:
      return className + 'State-Pending';
  }
};

const stateIcons = {
  rejected: < BlockIcon className={getClassNameByState(EquivalencyState.rejected)} />,
  aproved: <AddTaskIcon className={getClassNameByState(EquivalencyState.aproved)} />,
  pending: <AccessTimeIcon className={getClassNameByState(EquivalencyState.pending)} />,
  default: <QuestionMarkIcon className={getClassNameByState(EquivalencyState.rejected)} />
};

const RequestActivityRow = ({
  row,
  activitiesExpanded,
  setActivitiesExpanded,
  editFiles,
  addFile,
  removeFile,
  isEditFile,
  getMaxSize,
}) => {
  const { t } = useTranslation();
  const [activity] = useState(row.activity);
  const [state] = useState(row.state);
  const [openRejectedDialog, setOpenRejectedDialog] = useState(false);
  const [rejectedComment, setRejectedComment] = useState(null);

  const program_file = useMemo(() => {
    return row?.program_file;
  }, [editFiles]);

  const expandActivity = () => {
    const addRow = [...activitiesExpanded, row.id];
    setActivitiesExpanded(addRow);
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const handleChangeExpanded = () => {
    const isActivityExpanded = activitiesExpanded?.includes(row.id);
    if (isActivityExpanded) {
      collapseActivity();
    } else {
      expandActivity();
    }
  };

  const collapseActivity = () => {
    const deleteRow = activitiesExpanded.filter(a => a !== row.id);
    setActivitiesExpanded(deleteRow);
  };

  const checkExpanded = () => {
    return activitiesExpanded?.includes(row.id);
  };

  const classNameArrowIcon = () => {
    return checkExpanded() ? 'expanded-all' : 'collapse-all';
  };

  const getIconByState = (state) => {
    switch (state) {
      case EquivalencyState.rejected: return stateIcons.rejected;
      case EquivalencyState.aproved: return stateIcons.aproved;
      case EquivalencyState.pending: return stateIcons.pending;
      default: return stateIcons.default;
    }
  };

  const getText = () => {
    if (state === EquivalencyState.aproved) {
      return t('approvedEquivalencyRequestTitle');
    } else if (state === EquivalencyState.rejected) {
      return t('rejectedEquivalencyRequestTitle');
    } else if (state === EquivalencyState.pending) {
      return t('pendingEquivalencyRequestTitle');
    } else if (state === EquivalencyState.review) {
      return t('inReviewEquivalencyRequestTitle');
    }
  };

  const showComment = (comment) => {
    setRejectedComment(comment)
    setOpenRejectedDialog(true)
  }

  return (
    <>
      <TableRow sx={{ display: 'table-row' }} size="small">
        <TableCell id='RequestActivityCell' >
          <Box sx={{ backgroundColor: 'white' }}>
            <ListItem size='small'>
              <Button
                className={`EQXXI-RequestActivityRow-Switch ${classNameArrowIcon()}`}
                onClick={handleChangeExpanded}
                data-testid={'expended-button'}>
                <ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />
              </Button>
              {(activity?.id == null || activity?.id === '')
                ? <ListItemAvatar>
                  <Tooltip title={t('unknowActivity')}>{stateIcons.default}</Tooltip>
                  {state !== EquivalencyState.pending &&
                    <Tooltip title={getText()}>{getIconByState(state)}</Tooltip>
                  }
                </ListItemAvatar>
                : <ListItemAvatar>
                  <Tooltip title={getText()}>{getIconByState(state)}</Tooltip>
                </ListItemAvatar>
              }
              <ListItemText>
                <div className="EQXXI-Request-ActivityAccordion-Title">
                  <div>
                    <span style={{ color: '#006c5b' }}>{activity?.name}</span> <span>
                      {activity?.code ? `- ${activity?.code}` : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {editFiles && program_file ?
                      <RequestFileRow
                        documentId={program_file.id}
                        url={program_file.url}
                        title={program_file.file_type}
                        documentName={program_file.file_name}
                        addFile={addFile}
                        removeFile={removeFile}
                        canUpdate={isEditFile}
                        isLast={false}
                        documentType={program_file.file_type}
                        maxSize={getMaxSize(document)}
                        documents={program_file}
                      />
                      : program_file ? <FileReaderProvider>
                        <FileReaderButton
                          key={program_file?.file_name}
                          file={program_file}
                          variant={'outlined'}
                        />
                      </FileReaderProvider>
                        : null
                    }
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '20px' }} >{t('grade').toUpperCase()}:
                      <div style={{ width: '1rem', fontWeight: 'bold' }}>{row?.grade}</div>
                      <div className="EQXXI-Request-ActivityAccordion-RequiredCheck">
                        {state === EquivalencyState.pending}
                      </div>
                      {row.state === 'REJECTED' &&
                        <Tooltip title={t('rejectedShowComment')}>
                          <IconButton
                            id='showComment'
                            data-testid="show-comment-btn"
                            className="EQXXI-RequestSimulateRow-Button RejectAction"
                            onClick={() => showComment(row.rejectionReason)}
                          >
                            <Info />
                          </IconButton>
                        </Tooltip>
                      }
                    </div>
                  </div>
                </div>
              </ListItemText>
            </ListItem>
          </Box>
          <Collapse in={checkExpanded()}>
            <Box className="EQXXI-RequestTranscription-Column">
              <TextField
                label={t('grade')}
                fullWidth
                className="EQXXI-RequestTranscription-Row-Grade opacity-disable"
                variant="standard"
                value={row?.grade}
                type="text"
                disabled
                id='grade-input'
                data-testid={'grade-input'}
              />
              <TextField
                label={t('approvedDate')}
                fullWidth
                className="EQXXI-RequestTranscription-Row-Date opacity-disable"
                variant="standard"
                value={row?.approval_date}
                type='text'
                disabled
                id='approvedDate-input'
                data-testid={'approvedDate-input'}
              />
              <TextField
                label={t('page')}
                fullWidth
                className="EQXXI-RequestTranscription-Row-Page opacity-disable"
                variant="standard"
                value={row?.academic_transcript?.page}
                disabled
                id='page-input'
                data-testid={'page-input'}
              />
              <TextField
                label={t('book')}
                fullWidth
                className="EQXXI-RequestTranscription-Row-Book opacity-disable"
                variant="standard"
                value={row?.academic_transcript?.book}
                disabled
                id='book-input'
                data-testid={'book-input'}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow >
      <InfoDialog
        open={openRejectedDialog}
        title={t('rejectedCommentDialogTitle')}
        message={rejectedComment}
        onClose={() => {
          setOpenRejectedDialog(false)
          setRejectedComment(null)
        }}
      />
    </>
  );
};

RequestActivityRow.propTypes = {
  row: PropTypes.object,
  activitiesExpanded: PropTypes.object,
  setActivitiesExpanded: PropTypes.func,
  editFiles: PropTypes.bool,
  addFile: PropTypes.func,
  removeFile: PropTypes.func,
  isEditFile: PropTypes.bool,
  getMaxSize: PropTypes.func,
};

export default RequestActivityRow;
